import { readText, removeLocalValue, writeText } from '#lib/platform/local-json.js';

const MINDTECH_HOMESERVER = 'https://play.fablemedia.tech';
const PAUSED_KEY = 'sable-mindtech-login-paused';

// Only the office deployment is branded; other single-provider installs keep upstream auth.
export function isMindtechProvider(config: {
  list: readonly string[];
  allowCustom: boolean;
}): boolean {
  return (
    !config.allowCustom &&
    config.list.length === 1 &&
    (config.list[0] === MINDTECH_HOMESERVER || config.list[0] === `${MINDTECH_HOMESERVER}/`)
  );
}

// Non-secret suppression, shared with logout. A failed/cancelled attempt must not start itself again.
// These helpers never carry OAuth secrets; Core owns the durable pending authorization.
export function pauseMindtechLogin(): void {
  writeText(PAUSED_KEY, 'true');
  try {
    sessionStorage.setItem(PAUSED_KEY, 'true');
  } catch {
    /* Fail closed at launch. */
  }
}

export function mindtechLoginPaused(): boolean {
  try {
    return (
      readText(PAUSED_KEY) === 'true' ||
      (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(PAUSED_KEY) === 'true')
    );
  } catch {
    return true;
  }
}

export function clearMindtechLoginPause(): void {
  removeLocalValue(PAUSED_KEY);
  try {
    sessionStorage.removeItem(PAUSED_KEY);
  } catch {
    /* Optional preference. */
  }
}

const CONTEXT_KEY = 'sable-mindtech-redirect-context';
// Bridge an RPC result to durable UI storage, including a failed write. Never keep OAuth secrets.
let unpersistedContext: string | null = null;

export function isRedirectContextId(value: unknown): value is string {
  return typeof value === 'string' && value.length <= 1024 && value.trim().length > 0;
}

export function redirectStorageAvailable(): boolean {
  try {
    const probe = `${CONTEXT_KEY}.probe`;
    sessionStorage.setItem(probe, '1');
    const available = sessionStorage.getItem(probe) === '1';
    sessionStorage.removeItem(probe);
    return available;
  } catch {
    return false;
  }
}

// Only an opaque Core handle crosses navigation. PKCE/state/issuer/store stay in the Core registry.
export function saveRedirectContext(contextId: string): boolean {
  if (!isRedirectContextId(contextId)) return false;
  unpersistedContext = contextId;
  try {
    sessionStorage.setItem(CONTEXT_KEY, contextId);
    const persisted = sessionStorage.getItem(CONTEXT_KEY) === contextId;
    if (persisted) unpersistedContext = null;
    return persisted;
  } catch {
    return false;
  }
}

export function readRedirectContext(): string | null {
  if (unpersistedContext) return unpersistedContext;
  if (typeof sessionStorage === 'undefined') return null;
  // An unreadable marker is not evidence that there is no pending Core authorization.
  const value = sessionStorage.getItem(CONTEXT_KEY);
  return isRedirectContextId(value) ? value : null;
}

// Clear only after Core acknowledged completion/cancellation, and never erase a newer context.
// Storage errors propagate: a caller must not start another authorization with an uncleared ID.
export function clearRedirectContext(contextId: string): void {
  if (sessionStorage.getItem(CONTEXT_KEY) === contextId) {
    sessionStorage.removeItem(CONTEXT_KEY);
    if (sessionStorage.getItem(CONTEXT_KEY) === contextId)
      throw new Error('Redirect context not cleared');
  }
  if (unpersistedContext === contextId) unpersistedContext = null;
}

export async function cancelRedirectContext(
  contextId: string,
  cancel: (contextId: string) => Promise<void>
): Promise<void> {
  await cancel(contextId);
  clearRedirectContext(contextId);
}

export function carriesAuthResponse(url: URL): boolean {
  const fragment = new URLSearchParams(url.hash.slice(1));
  return ['code', 'state', 'error', 'loginToken'].some(
    (key) => url.searchParams.has(key) || fragment.has(key)
  );
}
